import { TRADE_NPC_CLASS_IDS } from "../../map/map-trade-npc-contract.js";

export const MAX_MAP_PRESET_PAYLOAD_BYTES_V1 = 256 * 1024;
export const MAX_MAP_PRESET_IDENTIFIER_CHARS_V1 = 256;
export const MAX_MAP_PRESET_COLLECTION_ITEMS_V1 = 8_192;
export const MAX_MAP_PRESET_SEARCH_DEPTH_V1 = 32;
export const MAX_MAP_PRESET_SEARCH_NODES_V1 = 8_192;
export const MAX_MAP_PRESET_SEARCH_LIST_ITEMS_V1 = 8_192;
export const MAX_MAP_PRESET_SEARCH_STRING_CHARS_V1 = 256;
export const MAX_MAP_PRESET_SEARCH_TOTAL_STRING_CHARS_V1 = 64 * 1024;
export const MAX_MAP_PRESET_SEARCH_BYTES_V1 = 128 * 1024;
export const MAX_MAP_PRESET_ABSOLUTE_NUMBER_V1 = 1_000_000_000_000;
export const MAP_PRESET_GLOBAL_POINT_ICON_SCALE_RANGE_V1 = Object.freeze([1, 5]);
export const MAP_PRESET_LAYER_POINT_ICON_SCALE_RANGE_V1 =
  MAP_PRESET_GLOBAL_POINT_ICON_SCALE_RANGE_V1;

const JSON_TEXT_ENCODER = new TextEncoder();
const I32_MIN = -2_147_483_648;
const I32_MAX = 2_147_483_647;
const U32_MAX = 4_294_967_295;

const VIEW_MODES = new Set(["2d", "3d"]);
const LOOT_PROFILES = new Set(["rod", "harpoon", "unassigned"]);
const SEARCH_EXPRESSION_OPERATORS = new Set(["and", "or"]);
const SEARCH_PATCH_BOUNDS = new Set(["from", "to"]);
const SEARCH_STATUSES = new Set(["favourite", "missing", "missing-titles"]);
const SEARCH_GRADES = new Set(["red", "yellow", "blue", "green", "white"]);
const SEARCH_SOURCE_KINDS = new Set([
  "rod",
  "zone",
  "hotspot",
  "harpoon",
  "ice_fishing",
]);
const SEARCH_FACT_OPERATORS = new Set([
  "exists",
  "eq",
  "eq-ci",
  "ne",
  "gt",
  "gte",
  "lt",
  "lte",
  "contains-ci",
]);
const SEARCH_COMPARE_OPERATORS = new Set(["eq", "eq-ci", "ne", "gt", "gte", "lt", "lte"]);
const SEARCH_MEMBERSHIP_OPERATORS = new Set(["in", "not-in"]);
const TRADE_NPC_CLASSES = new Set(TRADE_NPC_CLASS_IDS);

function isPlainRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === null || prototype === Object.prototype;
}

function requireExactFields(value, fields, label) {
  if (!isPlainRecord(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  const actual = Object.keys(value).sort();
  const expected = [...fields].sort();
  if (
    actual.length !== expected.length
    || actual.some((field, index) => field !== expected[index])
  ) {
    throw new TypeError(`${label} has unsupported or missing fields`);
  }
  return value;
}

function requireAllowedFields(value, fields, label) {
  if (!isPlainRecord(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  for (const field of Object.keys(value)) {
    if (!fields.has(field)) {
      throw new TypeError(`${label} has unsupported field ${field}`);
    }
  }
  return value;
}

function requireBoolean(value, label) {
  if (typeof value !== "boolean") {
    throw new TypeError(`${label} must be a boolean`);
  }
}

function requireEnum(value, allowed, label) {
  if (typeof value !== "string" || !allowed.has(value)) {
    throw new TypeError(`${label} is unsupported`);
  }
}

function requireIntegerRange(value, min, max, label) {
  if (!Number.isInteger(value) || value < min || value > max) {
    throw new TypeError(`${label} must be an integer from ${min} through ${max}`);
  }
}

function requirePositiveI32(value, label) {
  requireIntegerRange(value, 1, I32_MAX, label);
}

function requireU32(value, label, { positive = false } = {}) {
  requireIntegerRange(value, positive ? 1 : 0, U32_MAX, label);
}

function requireFiniteRange(value, min, max, label) {
  if (typeof value !== "number" || !Number.isFinite(value) || value < min || value > max) {
    throw new TypeError(`${label} is outside its supported finite range`);
  }
}

function unicodeScalarCount(value, label) {
  if (typeof value !== "string") {
    throw new TypeError(`${label} must be a string`);
  }
  let count = 0;
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit >= 0xD800 && codeUnit <= 0xDBFF) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xDC00 && next <= 0xDFFF)) {
        throw new TypeError(`${label} must contain valid Unicode scalar values`);
      }
      index += 1;
    } else if (codeUnit >= 0xDC00 && codeUnit <= 0xDFFF) {
      throw new TypeError(`${label} must contain valid Unicode scalar values`);
    }
    count += 1;
  }
  return count;
}

function requireIdentifier(value, label, { allowEmpty = false } = {}) {
  const chars = unicodeScalarCount(value, label);
  if (value.trim() !== value || (!allowEmpty && value.length === 0)) {
    throw new TypeError(`${label} must be ${allowEmpty ? "trimmed" : "non-empty and trimmed"}`);
  }
  if (chars > MAX_MAP_PRESET_IDENTIFIER_CHARS_V1) {
    throw new TypeError(`${label} exceeds its character limit`);
  }
}

function requireCollection(value, label) {
  if (!Array.isArray(value)) {
    throw new TypeError(`${label} must be an array`);
  }
  if (value.length > MAX_MAP_PRESET_COLLECTION_ITEMS_V1) {
    throw new TypeError(`${label} exceeds its item limit`);
  }
  return value;
}

function requireUniqueIdentifiers(value, label) {
  const seen = new Set();
  for (const [index, entry] of requireCollection(value, label).entries()) {
    requireIdentifier(entry, `${label}[${index}]`);
    if (seen.has(entry)) {
      throw new TypeError(`${label} contains a duplicate identifier`);
    }
    seen.add(entry);
  }
}

function requireRecord(value, label, validateValue) {
  if (!isPlainRecord(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  const entries = Object.entries(value);
  if (entries.length > MAX_MAP_PRESET_COLLECTION_ITEMS_V1) {
    throw new TypeError(`${label} exceeds its item limit`);
  }
  for (const [key, entry] of entries) {
    requireIdentifier(key, `${label} key`);
    validateValue(entry, `${label}.${key}`);
  }
}

function requireWindowEntry(value, label) {
  const entry = requireExactFields(value, ["open", "collapsed", "x", "y"], label);
  requireBoolean(entry.open, `${label}.open`);
  requireBoolean(entry.collapsed, `${label}.collapsed`);
  for (const field of ["x", "y"]) {
    if (entry[field] !== null) {
      requireIntegerRange(entry[field], I32_MIN, I32_MAX, `${label}.${field}`);
    }
  }
}

function requireWindowUi(value) {
  const windowUi = requireExactFields(
    value,
    ["search", "help", "settings", "zoneInfo", "layers", "bookmarks"],
    "Map preset window UI",
  );
  requireWindowEntry(windowUi.search, "Map preset search window");
  requireWindowEntry(windowUi.help, "Map preset help window");
  requireWindowEntry(windowUi.layers, "Map preset layers window");
  requireWindowEntry(windowUi.bookmarks, "Map preset bookmarks window");

  const settings = requireExactFields(
    windowUi.settings,
    [
      "x",
      "y",
      "autoAdjustView",
      "sampleFromFilteredElements",
      "threeDEnabled",
      "effectsEnabled",
      "focusWavesEnabled",
    ],
    "Map preset settings window",
  );
  for (const field of ["x", "y"]) {
    if (settings[field] !== null) {
      requireIntegerRange(settings[field], I32_MIN, I32_MAX, `Map preset settings.${field}`);
    }
  }
  for (const field of [
    "autoAdjustView",
    "sampleFromFilteredElements",
    "threeDEnabled",
    "effectsEnabled",
    "focusWavesEnabled",
  ]) {
    requireBoolean(settings[field], `Map preset settings.${field}`);
  }

  const zoneInfo = requireExactFields(
    windowUi.zoneInfo,
    ["open", "collapsed", "x", "y", "tab", "lootProfile", "hotspotModelTab"],
    "Map preset zone-info window",
  );
  requireBoolean(zoneInfo.open, "Map preset zone-info.open");
  requireBoolean(zoneInfo.collapsed, "Map preset zone-info.collapsed");
  for (const field of ["x", "y"]) {
    if (zoneInfo[field] !== null) {
      requireIntegerRange(zoneInfo[field], I32_MIN, I32_MAX, `Map preset zone-info.${field}`);
    }
  }
  requireIdentifier(zoneInfo.tab, "Map preset zone-info.tab", { allowEmpty: true });
  requireEnum(zoneInfo.lootProfile, LOOT_PROFILES, "Map preset zone-info.lootProfile");
  requireIdentifier(
    zoneInfo.hotspotModelTab,
    "Map preset zone-info.hotspotModelTab",
    { allowEmpty: true },
  );
}

function requireLayers(value) {
  const layers = requireExactFields(
    value,
    [
      "expandedLayerIds",
      "hoverFactsVisibleByLayer",
      "sampleHoverVisibleByLayer",
      "lineagePanelVisibleByLayer",
    ],
    "Map preset layers",
  );
  requireUniqueIdentifiers(layers.expandedLayerIds, "Map preset expanded layer IDs");
  requireRecord(
    layers.hoverFactsVisibleByLayer,
    "Map preset hover-fact visibility",
    (facts, label) => requireRecord(facts, label, requireBoolean),
  );
  requireRecord(
    layers.sampleHoverVisibleByLayer,
    "Map preset sample-hover visibility",
    requireBoolean,
  );
  requireRecord(
    layers.lineagePanelVisibleByLayer,
    "Map preset lineage-panel visibility",
    requireBoolean,
  );
}

class SearchBudget {
  nodes = 0;

  totalStringChars = 0;

  active = new WeakSet();

  node(value, depth, label) {
    if (depth > MAX_MAP_PRESET_SEARCH_DEPTH_V1) {
      throw new TypeError("Map preset search exceeds its nesting-depth limit");
    }
    this.nodes += 1;
    if (this.nodes > MAX_MAP_PRESET_SEARCH_NODES_V1) {
      throw new TypeError("Map preset search exceeds its node limit");
    }
    if (this.active.has(value)) {
      throw new TypeError(`${label} must not contain a cycle`);
    }
    this.active.add(value);
  }

  leave(value) {
    this.active.delete(value);
  }

  string(value, label) {
    const chars = unicodeScalarCount(value, label);
    if (!value.length || value.trim() !== value) {
      throw new TypeError(`${label} must be non-empty and trimmed`);
    }
    if (chars > MAX_MAP_PRESET_SEARCH_STRING_CHARS_V1) {
      throw new TypeError(`${label} exceeds its character limit`);
    }
    this.totalStringChars += chars;
    if (this.totalStringChars > MAX_MAP_PRESET_SEARCH_TOTAL_STRING_CHARS_V1) {
      throw new TypeError("Map preset search exceeds its total character limit");
    }
  }

  list(value, label) {
    if (!Array.isArray(value)) {
      throw new TypeError(`${label} must be an array`);
    }
    if (value.length > MAX_MAP_PRESET_SEARCH_LIST_ITEMS_V1) {
      throw new TypeError(`${label} exceeds its item limit`);
    }
    return value;
  }
}

function requireOptionalBoolean(value, field, label) {
  if (Object.hasOwn(value, field)) {
    requireBoolean(value[field], `${label}.${field}`);
  }
}

function requireSearchSourceKinds(value, label, budget) {
  const sourceKinds = budget.list(value, label);
  let previous = null;
  for (const [index, sourceKind] of sourceKinds.entries()) {
    requireEnum(sourceKind, SEARCH_SOURCE_KINDS, `${label}[${index}]`);
    if (previous !== null && previous >= sourceKind) {
      throw new TypeError(`${label} must be sorted and unique`);
    }
    previous = sourceKind;
  }
}

function requirePositiveI32List(value, label, budget) {
  const ids = budget.list(value, label);
  if (ids.length === 0) {
    throw new TypeError(`${label} must not be empty`);
  }
  let previous = null;
  for (const [index, id] of ids.entries()) {
    requirePositiveI32(id, `${label}[${index}]`);
    if (previous !== null && previous >= id) {
      throw new TypeError(`${label} must be sorted and unique`);
    }
    previous = id;
  }
}

function requireSearchFactCondition(value, budget, depth) {
  const label = "Map preset search fact condition";
  const condition = requireAllowedFields(
    value,
    new Set(["path", "op", "value", "tolerance"]),
    label,
  );
  budget.node(condition, depth, label);
  try {
    if (!Object.hasOwn(condition, "path") || !Object.hasOwn(condition, "op")) {
      throw new TypeError(`${label} has missing fields`);
    }
    budget.string(condition.path, `${label}.path`);
    requireEnum(condition.op, SEARCH_FACT_OPERATORS, `${label}.op`);
    const hasValue = Object.hasOwn(condition, "value");
    if (condition.op === "exists" && hasValue) {
      throw new TypeError("Map preset exists condition must omit value");
    }
    if (condition.op !== "exists" && !hasValue) {
      throw new TypeError("Map preset comparison condition requires value");
    }
    if (hasValue) {
      const entry = condition.value;
      if (typeof entry === "string") {
        budget.string(entry, `${label}.value`);
      } else if (typeof entry === "number") {
        requireFiniteRange(
          entry,
          -MAX_MAP_PRESET_ABSOLUTE_NUMBER_V1,
          MAX_MAP_PRESET_ABSOLUTE_NUMBER_V1,
          `${label}.value`,
        );
      } else if (typeof entry !== "boolean") {
        throw new TypeError(`${label}.value must be a string, number, or boolean`);
      }
    }
    if (Object.hasOwn(condition, "tolerance")) {
      requireFiniteRange(
        condition.tolerance,
        0,
        MAX_MAP_PRESET_ABSOLUTE_NUMBER_V1,
        `${label}.tolerance`,
      );
    }
  } finally {
    budget.leave(condition);
  }
}

function requireSearchSubject(value, budget, label) {
  if (!isPlainRecord(value) || typeof value.kind !== "string") {
    throw new TypeError(`${label} must be a supported subject object`);
  }
  if (value.kind === "fish") {
    requireExactFields(value, ["kind", "fishId"], label);
    requirePositiveI32(value.fishId, `${label}.fishId`);
  } else if (value.kind === "item") {
    requireExactFields(value, ["kind", "itemId"], label);
    requirePositiveI32(value.itemId, `${label}.itemId`);
  } else if (value.kind === "item-group") {
    requireExactFields(value, ["kind", "itemIds"], label);
    requirePositiveI32List(value.itemIds, `${label}.itemIds`, budget);
  } else if (value.kind === "zone") {
    requireExactFields(value, ["kind", "zoneRgb"], label);
    requireU32(value.zoneRgb, `${label}.zoneRgb`, { positive: true });
  } else {
    throw new TypeError(`${label}.kind is unsupported`);
  }
}

function requireSearchPercentValue(value, label) {
  const percent = requireExactFields(value, ["kind", "basisPoints"], label);
  if (percent.kind !== "percent") {
    throw new TypeError(`${label}.kind is unsupported`);
  }
  requireU32(percent.basisPoints, `${label}.basisPoints`);
}

function requireSearchRateComparison(value, label) {
  const comparison = requireExactFields(value, ["op", "value"], label);
  requireEnum(comparison.op, SEARCH_COMPARE_OPERATORS, `${label}.op`);
  requireSearchPercentValue(comparison.value, `${label}.value`);
}

function requireSearchTypedValue(value, budget, label) {
  if (!isPlainRecord(value) || typeof value.kind !== "string") {
    throw new TypeError(`${label} must be a supported typed value`);
  }
  if (value.kind === "number") {
    requireExactFields(value, ["kind", "value"], label);
    requireFiniteRange(
      value.value,
      -MAX_MAP_PRESET_ABSOLUTE_NUMBER_V1,
      MAX_MAP_PRESET_ABSOLUTE_NUMBER_V1,
      `${label}.value`,
    );
  } else if (value.kind === "boolean") {
    requireExactFields(value, ["kind", "value"], label);
    requireBoolean(value.value, `${label}.value`);
  } else if (value.kind === "text") {
    requireExactFields(value, ["kind", "value"], label);
    budget.string(value.value, `${label}.value`);
  } else if (value.kind === "percent") {
    requireSearchPercentValue(value, label);
  } else {
    throw new TypeError(`${label}.kind is unsupported`);
  }
}

function requireSearchPredicate(value, budget, depth) {
  const label = "Map preset search layer-filter predicate";
  if (!isPlainRecord(value) || typeof value.kind !== "string") {
    throw new TypeError(`${label} must be a supported predicate object`);
  }
  budget.node(value, depth, label);
  try {
    if (value.kind === "bool") {
      requireAllowedFields(value, new Set(["kind", "op", "children", "negated"]), label);
      if (!Object.hasOwn(value, "op") || !Object.hasOwn(value, "children")) {
        throw new TypeError(`${label} has missing fields`);
      }
      requireEnum(value.op, SEARCH_EXPRESSION_OPERATORS, `${label}.op`);
      requireOptionalBoolean(value, "negated", label);
      const children = budget.list(value.children, `${label}.children`);
      if (children.length === 0) {
        throw new TypeError(`${label}.children must not be empty`);
      }
      for (const child of children) {
        requireSearchPredicate(child, budget, depth + 1);
      }
    } else if (value.kind === "subject-presence") {
      requireExactFields(value, ["kind", "subject"], label);
      requireSearchSubject(value.subject, budget, `${label}.subject`);
    } else if (value.kind === "membership") {
      requireAllowedFields(value, new Set(["kind", "subject", "group", "op", "rate"]), label);
      for (const field of ["subject", "group", "op"]) {
        if (!Object.hasOwn(value, field)) {
          throw new TypeError(`${label} has missing field ${field}`);
        }
      }
      requireSearchSubject(value.subject, budget, `${label}.subject`);
      budget.string(value.group, `${label}.group`);
      requireEnum(value.op, SEARCH_MEMBERSHIP_OPERATORS, `${label}.op`);
      if (Object.hasOwn(value, "rate")) {
        requireSearchRateComparison(value.rate, `${label}.rate`);
      }
    } else if (value.kind === "group-rate") {
      requireExactFields(value, ["kind", "group", "comparison"], label);
      budget.string(value.group, `${label}.group`);
      requireSearchRateComparison(value.comparison, `${label}.comparison`);
    } else if (value.kind === "property-compare") {
      requireExactFields(value, ["kind", "field", "op", "value"], label);
      budget.string(value.field, `${label}.field`);
      requireEnum(value.op, SEARCH_COMPARE_OPERATORS, `${label}.op`);
      requireSearchTypedValue(value.value, budget, `${label}.value`);
    } else if (value.kind === "relation") {
      requireExactFields(value, ["kind", "relation", "target"], label);
      budget.string(value.relation, `${label}.relation`);
      requireSearchSubject(value.target, budget, `${label}.target`);
    } else {
      throw new TypeError(`${label}.kind is unsupported`);
    }
  } finally {
    budget.leave(value);
  }
}

function requireSearchTerm(value, budget, depth) {
  const label = "Map preset search term";
  if (!isPlainRecord(value) || typeof value.kind !== "string") {
    throw new TypeError(`${label} must be a supported term object`);
  }
  budget.node(value, depth, label);
  try {
    if (value.kind === "title") {
      requireExactFields(value, ["kind", "titleId"], label);
      if (!Number.isInteger(value.titleId) || value.titleId <= 0 || value.titleId > 2_147_483_647) {
        throw new TypeError(`${label}.titleId must be a positive i32`);
      }
    } else if (value.kind === "fishing-contest") {
      requireExactFields(value, ["kind"], label);
    } else if (value.kind === "patch-bound") {
      requireAllowedFields(value, new Set(["kind", "bound", "patchId"]), label);
      if (!Object.hasOwn(value, "bound")) {
        throw new TypeError(`${label} has missing field bound`);
      }
      requireEnum(value.bound, SEARCH_PATCH_BOUNDS, `${label}.bound`);
      if (Object.hasOwn(value, "patchId")) {
        budget.string(value.patchId, `${label}.patchId`);
      }
    } else if (value.kind === "status") {
      requireExactFields(value, ["kind", "status"], label);
      requireEnum(value.status, SEARCH_STATUSES, `${label}.status`);
    } else if (value.kind === "grade") {
      requireExactFields(value, ["kind", "grade"], label);
      requireEnum(value.grade, SEARCH_GRADES, `${label}.grade`);
    } else if (value.kind === "fish") {
      requireExactFields(value, ["kind", "fishId"], label);
      requirePositiveI32(value.fishId, `${label}.fishId`);
    } else if (value.kind === "item") {
      requireAllowedFields(value, new Set(["kind", "itemId", "sourceKinds"]), label);
      if (!Object.hasOwn(value, "itemId")) {
        throw new TypeError(`${label} has missing field itemId`);
      }
      requirePositiveI32(value.itemId, `${label}.itemId`);
      if (Object.hasOwn(value, "sourceKinds")) {
        requireSearchSourceKinds(value.sourceKinds, `${label}.sourceKinds`, budget);
      }
    } else if (value.kind === "item-group") {
      requireAllowedFields(value, new Set(["kind", "itemIds", "sourceKinds"]), label);
      if (!Object.hasOwn(value, "itemIds")) {
        throw new TypeError(`${label} has missing field itemIds`);
      }
      requirePositiveI32List(value.itemIds, `${label}.itemIds`, budget);
      if (Object.hasOwn(value, "sourceKinds")) {
        requireSearchSourceKinds(value.sourceKinds, `${label}.sourceKinds`, budget);
      }
    } else if (value.kind === "zone") {
      requireExactFields(value, ["kind", "zoneRgb"], label);
      requireU32(value.zoneRgb, `${label}.zoneRgb`, { positive: true });
    } else if (value.kind === "semantic") {
      requireExactFields(value, ["kind", "layerId", "fieldId"], label);
      budget.string(value.layerId, `${label}.layerId`);
      requireU32(value.fieldId, `${label}.fieldId`, { positive: true });
    } else if (value.kind === "layer-fact") {
      requireAllowedFields(value, new Set(["kind", "layerId", "factId", "conditions"]), label);
      for (const field of ["layerId", "factId"]) {
        if (!Object.hasOwn(value, field)) {
          throw new TypeError(`${label} has missing field ${field}`);
        }
        budget.string(value[field], `${label}.${field}`);
      }
      if (Object.hasOwn(value, "conditions")) {
        for (const condition of budget.list(value.conditions, `${label}.conditions`)) {
          requireSearchFactCondition(condition, budget, depth + 1);
        }
      }
    } else if (value.kind === "layer-filter") {
      requireExactFields(value, ["kind", "layerId", "predicate"], label);
      budget.string(value.layerId, `${label}.layerId`);
      requireSearchPredicate(value.predicate, budget, depth + 1);
    } else {
      throw new TypeError(`${label}.kind is unsupported`);
    }
  } finally {
    budget.leave(value);
  }
}

function requireSearchNode(value, budget, depth) {
  const label = "Map preset search expression node";
  if (!isPlainRecord(value) || typeof value.type !== "string") {
    throw new TypeError(`${label} must be a supported expression object`);
  }
  budget.node(value, depth, label);
  try {
    if (value.type === "term") {
      requireAllowedFields(value, new Set(["type", "term", "negated"]), label);
      if (!Object.hasOwn(value, "term")) {
        throw new TypeError(`${label} has missing field term`);
      }
      requireOptionalBoolean(value, "negated", label);
      requireSearchTerm(value.term, budget, depth + 1);
    } else if (value.type === "group") {
      requireAllowedFields(value, new Set(["type", "operator", "children", "negated"]), label);
      if (!Object.hasOwn(value, "operator") || !Object.hasOwn(value, "children")) {
        throw new TypeError(`${label} has missing fields`);
      }
      requireEnum(value.operator, SEARCH_EXPRESSION_OPERATORS, `${label}.operator`);
      requireOptionalBoolean(value, "negated", label);
      for (const child of budget.list(value.children, `${label}.children`)) {
        requireSearchNode(child, budget, depth + 1);
      }
    } else if (value.type === "container") {
      requireAllowedFields(value, new Set(["type", "expression", "negated"]), label);
      if (!Object.hasOwn(value, "expression")) {
        throw new TypeError(`${label} has missing field expression`);
      }
      requireOptionalBoolean(value, "negated", label);
      requireSearchNode(value.expression, budget, depth + 1);
    } else {
      throw new TypeError(`${label}.type is unsupported`);
    }
  } finally {
    budget.leave(value);
  }
}

function requireSearch(value) {
  const search = requireExactFields(value, ["expression"], "Map preset search");
  requireSearchNode(search.expression, new SearchBudget(), 1);
  if (JSON_TEXT_ENCODER.encode(JSON.stringify(search)).byteLength > MAX_MAP_PRESET_SEARCH_BYTES_V1) {
    throw new TypeError("Map preset search exceeds its byte limit");
  }
}

function requireBridgedUi(value) {
  const bridgedUi = requireExactFields(
    value,
    ["showPoints", "showPointIcons", "viewMode", "pointIconScale"],
    "Map preset bridged UI",
  );
  requireBoolean(bridgedUi.showPoints, "Map preset bridged UI.showPoints");
  requireBoolean(bridgedUi.showPointIcons, "Map preset bridged UI.showPointIcons");
  requireEnum(bridgedUi.viewMode, VIEW_MODES, "Map preset bridged UI.viewMode");
  requireFiniteRange(
    bridgedUi.pointIconScale,
    MAP_PRESET_GLOBAL_POINT_ICON_SCALE_RANGE_V1[0],
    MAP_PRESET_GLOBAL_POINT_ICON_SCALE_RANGE_V1[1],
    "Map preset bridged UI.pointIconScale",
  );
}

function requireHighlight(value, label) {
  const highlight = requireExactFields(value, ["enabled", "opacity", "color"], label);
  requireBoolean(highlight.enabled, `${label}.enabled`);
  requireFiniteRange(highlight.opacity, 0, 1, `${label}.opacity`);
  if (!Array.isArray(highlight.color) || highlight.color.length !== 3) {
    throw new TypeError(`${label}.color must contain exactly three channels`);
  }
  highlight.color.forEach((channel, index) => {
    requireIntegerRange(channel, 0, 255, `${label}.color[${index}]`);
  });
}

function requireBridgedFilters(value) {
  const filters = requireExactFields(
    value,
    [
      "layerIdsVisible",
      "layerIdsOrdered",
      "layerFilterBindingIdsDisabledByLayer",
      "layerOpacities",
      "layerHighlights",
      "layerConfidenceShowcase",
      "layerClipMasks",
      "layerWaypointConnectionsVisible",
      "layerWaypointLabelsVisible",
      "layerPointIconsVisible",
      "layerPointIconScales",
      "layerPointClusteringEnabled",
      "tradeNpcClassesVisible",
      "filterOnlyActiveConditionGroups",
      "lootConditionSelection",
    ],
    "Map preset bridged filters",
  );
  requireUniqueIdentifiers(filters.layerIdsVisible, "Map preset visible layer IDs");
  requireUniqueIdentifiers(filters.layerIdsOrdered, "Map preset ordered layer IDs");
  requireRecord(
    filters.layerFilterBindingIdsDisabledByLayer,
    "Map preset disabled filter bindings",
    requireUniqueIdentifiers,
  );
  requireRecord(filters.layerOpacities, "Map preset layer opacities", (entry, label) => {
    requireFiniteRange(entry, 0, 1, label);
  });
  requireRecord(filters.layerHighlights, "Map preset layer highlights", requireHighlight);
  for (const [field, label] of [
    ["layerConfidenceShowcase", "Map preset confidence showcase"],
    ["layerWaypointConnectionsVisible", "Map preset waypoint-connection visibility"],
    ["layerWaypointLabelsVisible", "Map preset waypoint-label visibility"],
    ["layerPointIconsVisible", "Map preset point-icon visibility"],
    ["layerPointClusteringEnabled", "Map preset point-clustering state"],
  ]) {
    requireRecord(filters[field], label, requireBoolean);
  }
  requireUniqueIdentifiers(
    filters.tradeNpcClassesVisible,
    "Map preset visible Trade NPC classes",
  );
  filters.tradeNpcClassesVisible.forEach((entry, index) => {
    requireEnum(
      entry,
      TRADE_NPC_CLASSES,
      `Map preset visible Trade NPC classes[${index}]`,
    );
  });
  requireRecord(filters.layerClipMasks, "Map preset layer clip masks", requireIdentifier);
  requireRecord(filters.layerPointIconScales, "Map preset layer point-icon scales", (entry, label) => {
    requireFiniteRange(
      entry,
      MAP_PRESET_LAYER_POINT_ICON_SCALE_RANGE_V1[0],
      MAP_PRESET_LAYER_POINT_ICON_SCALE_RANGE_V1[1],
      label,
    );
  });
  requireBoolean(
    filters.filterOnlyActiveConditionGroups,
    "Map preset filterOnlyActiveConditionGroups",
  );
  requireRecord(filters.lootConditionSelection, "Map preset loot-condition selection", (entry, label) => {
    requireU32(entry, label);
  });
}

function requireView(value) {
  const view = requireExactFields(value, ["viewMode", "camera"], "Map preset view");
  requireEnum(view.viewMode, VIEW_MODES, "Map preset view.viewMode");
  const camera = requireAllowedFields(
    view.camera,
    new Set([
      "centerWorldX",
      "centerWorldZ",
      "zoom",
      "pivotWorldX",
      "pivotWorldY",
      "pivotWorldZ",
      "yaw",
      "pitch",
      "distance",
    ]),
    "Map preset camera",
  );
  for (const [field, entry] of Object.entries(camera)) {
    requireFiniteRange(
      entry,
      -MAX_MAP_PRESET_ABSOLUTE_NUMBER_V1,
      MAX_MAP_PRESET_ABSOLUTE_NUMBER_V1,
      `Map preset camera.${field}`,
    );
    if ((field === "zoom" || field === "distance") && entry < 0) {
      throw new TypeError(`Map preset camera.${field} must be non-negative`);
    }
  }
}

export function assertMapPresetPayloadV1Bounds(value) {
  const payload = requireExactFields(
    value,
    ["version", "windowUi", "layers", "search", "bridgedUi", "bridgedFilters", "view"],
    "Map preset payload",
  );
  if (payload.version !== 1) {
    throw new TypeError("Map preset payload version must be 1");
  }
  requireWindowUi(payload.windowUi);
  requireLayers(payload.layers);
  requireSearch(payload.search);
  requireBridgedUi(payload.bridgedUi);
  requireBridgedFilters(payload.bridgedFilters);
  requireView(payload.view);
  if (JSON_TEXT_ENCODER.encode(JSON.stringify(payload)).byteLength > MAX_MAP_PRESET_PAYLOAD_BYTES_V1) {
    throw new TypeError("Map preset payload exceeds its byte limit");
  }
  return value;
}
